import './styles.scss'

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { Col, Row } from 'antd'
import clsx from 'clsx'

import B2BecLink from '@/components/B2BLink'
import NotFound from '@/components/NotFound'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { useAppSelector } from '@/store/hook'
import {
  getIsLoadingSubmenuProductCategoryList,
  getSubmenuProductCategoryFetchStatus,
  selectSubmenuProductCategories,
} from '@/store/selectors/categorySelector'
import { selectIsFuturetech } from '@/store/selectors/configSelector'
import { selectShowSparePartLink } from '@/store/selectors/userSelector'
import { useDeviceDetect } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { ACTION_STATUS, IMAGE_SOURCES, SHOP_CATEGORY_TYPE } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import missingImage from '@/assets/img/missing_image.webp'
import SparePartsImage from '@/assets/img/spare-parts-overview-page.webp'

import CategoryItem from './CategoryItem'

interface CategoryOverviewPageProps {
  title: string
  breadcrumbs: {
    title: string
    path: string
    dynamicTitle?: boolean
    dynamicPath?: boolean
  }[]
  pageId: string
}

function CategoryOverviewPage({
  pageId,
  title,
  breadcrumbs,
}: CategoryOverviewPageProps) {
  const { t, i18n } = useTranslation()
  const { isMobile } = useDeviceDetect()
  const navigate = useNavigate()

  const { type = SHOP_CATEGORY_TYPE?.MACHINES } = useParams()
  const productTypeLinkPrefix = type === SHOP_CATEGORY_TYPE?.MACHINES ? '' : `/${type}`
  const localeUpperCase = i18n.language?.toUpperCase()?.split('-')[1]

  useEffect(() => {
    // check if type is not one of known category types
    if (type && !Object.values(SHOP_CATEGORY_TYPE).includes(type)) {
      navigate('/404page')
    }
  }, [type, navigate])

  const titleData = []
  const breadcrumbsData = []
  if (type !== SHOP_CATEGORY_TYPE.MACHINES) {
    titleData.push(t(`pageTitle.${type}`))
    breadcrumbsData.push({
      titleData: [t(`breadcrumbTitle.${type}`)],
      pathData: [type],
    })
  }
  const shouldShowSparePart = useAppSelector(selectShowSparePartLink)
  const isFutureTech = useAppSelector(selectIsFuturetech)

  useDocumentTitle(title, titleData)
  useBreadcrumbs(breadcrumbs, breadcrumbsData)

  useAdobeAnalysis(null, {
    pageId,
    pageName: 'product categories',
    pageType: PAGE_TYPES.CATEGORIES,
  })

  const isLoading = useAppSelector(getIsLoadingSubmenuProductCategoryList)
  const fetchStatus = useAppSelector(getSubmenuProductCategoryFetchStatus)
  const productCategories = useAppSelector(selectSubmenuProductCategories)

  const [machines, accessories, detergents] = productCategories?.map(
    ({ categoryId }) => categoryId,
  )

  const CATEGORY_ID = useMemo(
    () => ({
      machines,
      accessories,
      detergents,
    }),
    [accessories, detergents, machines],
  )

  const categoryData = useMemo(() => {
    const id = CATEGORY_ID[type]

    return productCategories.find(({ categoryId }) => categoryId === id)
  }, [CATEGORY_ID, productCategories, type])

  const getCategoryImageURL = (category) => {
    if (isFutureTech) {
      return `${require(`../../../assets/img/futuretech/${category}.webp`).default}`
    }

    const imageType = category === 'accessories' ? 'ACCESSORIES' : 'DETERGENTS'
    return (
      IMAGE_SOURCES[localeUpperCase]?.[imageType]
      || IMAGE_SOURCES.DEFAULT[imageType]
    )
  }

  return fetchStatus === ACTION_STATUS.FAILED ? <NotFound /> : (
    <div
      className={clsx('category-overview__wrapper', {
        'pt-3': !isMobile,
      })}
    >
      <div className="container-full-width-page category-overview__heading">
        <div className="category-overview__heading__container">
          <h1 className="category-overview__title mb-5 mt-5">
            {t(`productCategoryOverview.${type}.title`)}
          </h1>
          <p className="category-overview__description mb-5">
            {t(`productCategoryOverview.${type}.description`)}
          </p>
        </div>
      </div>
      {isLoading ? <B2becCardSkeleton imageHeight={155.88} number={8} /> : (
        <>
          <Row gutter={[16, 32]}>
            {categoryData?.subCategories.map((category, index) => {
              let imageSource

              // update static image for machine's 7th category
              if (type === SHOP_CATEGORY_TYPE?.MACHINES && index === 7) {
                imageSource = missingImage
              } else {
                if (!isValidArray(category?.images)) {
                  imageSource = ''
                } else {
                  const { url } = category?.images[0]
                  imageSource = url
                }
              }

              return category?.categoryName && category?.categoryId
                ? (
                  <Col key={category.categoryId} lg={8} md={12} span={24} xl={6}>
                    <CategoryItem
                      categoryId={category.categoryId}
                      imgDescription={category.categoryName}
                      imgUrl={imageSource}
                      title={category.categoryName}
                      url={`${productTypeLinkPrefix}/category/${category.categoryId}`}
                      subCategories={categoryData.categoryId !== CATEGORY_ID.machines
                        ? []
                        : category.subCategories}
                    />
                  </Col>
                )
                : null
            })}
          </Row>
          {type === SHOP_CATEGORY_TYPE?.MACHINES
            ? (
              <>
                <h2 className="category-overview__middle-section__title mb-5 mt-6">
                  {t('productCategoryOverview.middleSection.title')}
                </h2>
                <Row gutter={[16, 32]}>
                  <Col lg={6} md={12} sm={24} xl={6} xs={24}>
                    <B2BecLink to="/categories/accessories">
                      <img
                        className="category-overview__middle-section__image"
                        src={getCategoryImageURL('accessories')}
                        alt={t(
                          'productCategoryOverview.middleSection.accessories',
                        )}
                      />
                      <h3 className="category-overview__middle-section__image__subtitle mt-2">
                        {t('productCategoryOverview.middleSection.accessories')}
                      </h3>
                    </B2BecLink>
                  </Col>
                  <Col lg={6} md={12} sm={24} xl={6} xs={24}>
                    <B2BecLink to="/categories/detergents">
                      <img
                        className="category-overview__middle-section__image"
                        src={getCategoryImageURL('detergents')}
                        alt={t(
                          'productCategoryOverview.middleSection.detergents',
                        )}
                      />
                      <h3 className="category-overview__middle-section__image__subtitle mt-2">
                        {t('productCategoryOverview.middleSection.detergents')}
                      </h3>
                    </B2BecLink>
                  </Col>
                  {shouldShowSparePart && (
                    <PermissionWrapper
                      permission={MODULE_SHOP_NAVIGATION_RIGHTS.OPEN_SPARE_PART_SHOP}
                    >
                      <Col lg={6} md={12} sm={24} xl={6} xs={24}>
                        <B2BecLink to="/spare-parts">
                          <div className="spare-parts">
                            <img
                              alt={t('searchResultsPage.header.spareParts')}
                              className="category-overview__middle-section__image"
                              src={SparePartsImage}
                            />
                            <h3 className="category-overview__middle-section__image__subtitle mt-2">
                              {t('searchResultsPage.header.spareParts')}
                            </h3>
                          </div>
                        </B2BecLink>
                      </Col>
                    </PermissionWrapper>
                  )}
                </Row>
              </>
            )
            : null}
        </>
      )}
    </div>
  )
}

export default CategoryOverviewPage
